import { memo } from 'react';
import { useTracking } from 'react-tracking';

import type { Company } from './types';

import { FeedCardItem } from './FeedCardItem';
import { FeedCardLink } from './FeedCardLink';
import { FlaggedFeedCardHeader as FeedCardHeader } from './FlaggedFeedCardHeader';
import { ImpressionTracking } from './ImpressionTracking';
import { NarrativeFeedCard } from './NarrativeFeedCard';

import { Wrapper, StyledFeedCards } from './styled';

export interface Props {
  index: number;
  company: Company;
  onIntersection?: (id: string) => void; // tracking callback
  'data-cy-id'?: string;
}

const FeedCardItemMemo = memo(FeedCardItem);

const FeedCard = ({
  index,
  company,
  onIntersection,
  'data-cy-id': dataCyId,
}: Props) => {
  const { companyId, tickerSymbol, feedItems } = company;
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'feed',
    companyId,
    tickerSymbol,
    cardIndex: index,
  });

  return (
    <Track>
      <Wrapper data-cy-id={dataCyId}>
        <FeedCardHeader
          company={company}
          onClick={() =>
            trackEvent({
              modifier: 'card-header',
              action: 'click',
            })
          }
        />
        <StyledFeedCards>
          {feedItems.map((feedItem, i) => {
            if (feedItem.type === 'narrative') {
              return (
                <NarrativeFeedCard
                  company={company}
                  feedItem={feedItem}
                  index={i}
                  key={`${i}-${feedItem.date}-${feedItem.url}`}
                  onIntersection={onIntersection}
                />
              );
            } else {
              return (
                <FeedCardLink
                  key={`${i}-${feedItem.date}-${feedItem.url}`}
                  index={i}
                  feedItem={feedItem}
                  company={company}
                >
                  <ImpressionTracking
                    id={feedItem.activityId}
                    onIntersection={onIntersection}
                  >
                    <FeedCardItemMemo feedItem={feedItem} />
                  </ImpressionTracking>
                </FeedCardLink>
              );
            }
          })}
        </StyledFeedCards>
      </Wrapper>
    </Track>
  );
};

export default FeedCard;
